import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { ErrorMessage, FieldError, Spinner } from '@/components/ui'
import { ApiError, api } from '@/lib/api'
import type { MeResponse, TokenResponse } from '@/lib/types'
import { useAuthStore } from '@/stores/auth'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const setAccessToken = useAuthStore((state) => state.setAccessToken)
  const setSession = useAuthStore((state) => state.setSession)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const login = useMutation({
    mutationFn: async () => {
      const tokens = await api.post<TokenResponse>(
        '/auth/login',
        { email, password },
        { skipWorkspace: true },
      )
      setAccessToken(tokens.access_token)
      // Профиль и список пространств нужны сразу: без них интерфейс не знает,
      // куда пускать пользователя и что показывать в переключателе
      return api.get<MeResponse>('/auth/me', { skipWorkspace: true })
    },
    onSuccess: (me) => {
      setSession(me.user, me.workspaces)
      const from = (location.state as { from?: string } | null)?.from
      void navigate(from && from !== '/login' ? from : '/', { replace: true })
    },
  })

  const fieldErrors = login.error instanceof ApiError ? login.error.fieldErrors : {}

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <span className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-600 text-base font-bold text-white">
            RF
          </span>
          <h1 className="text-2xl font-semibold text-slate-900">Вход в ReportFlow</h1>
          <p className="mt-1 text-sm text-slate-500">
            Управленческий дашборд, который собирается из ваших данных
          </p>
        </div>

        <form
          className="card space-y-4 p-6"
          onSubmit={(event) => {
            event.preventDefault()
            login.mutate()
          }}
        >
          <div>
            <label className="label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              className={`input ${fieldErrors.email ? 'input-error' : ''}`}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="director@company.ru"
            />
            <FieldError message={fieldErrors.email} />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Пароль
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              className={`input ${fieldErrors.password ? 'input-error' : ''}`}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <FieldError message={fieldErrors.password} />
          </div>

          <ErrorMessage error={login.error} />

          <button type="submit" className="btn-primary w-full" disabled={login.isPending}>
            {login.isPending && <Spinner className="h-4 w-4" />}
            Войти
          </button>

          <p className="text-center text-sm text-slate-500">
            Нет аккаунта?{' '}
            <Link to="/register" className="font-medium text-brand-600 hover:text-brand-700">
              Зарегистрироваться
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
